import { IAuction, IAuctionBid } from '@/types'
import { getApiBasepath } from 'api'
import { useAuth } from 'contexts'
import dayjs from 'dayjs'
import { useFeatureFlags } from 'hooks'
import qs from 'qs'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { StrapiPagination } from 'types'
import { determineAuctionStatus, getAuctionUrl } from 'utils/auctions'
import { FlatCollectionType, flatCollectionType } from 'utils/data'

export const defaultAuctionPageQuery = qs.stringify(
  {
    populate: {
      artworkImages: {
        fields: ['name', 'formats', 'url'],
      },
      highestBid: {
        fields: ['price', 'currency'],
      },
      bids: {
        filters: {
          status: { $eq: 'Approved' },
        },
        fields: ['price', 'currency', 'createdAt'],
        sort: ['createdAt:desc'],
      },
    },
  },
  { encodeValuesOnly: true }
)

interface GetAuctionsResponse {
  data: IAuction[]
  meta: { pagination: StrapiPagination }
}

export const getAuctions = async (query?: string) => {
  const response = await fetch(`${getApiBasepath()}/api/auctions?${query}`)
  const data = await response.json()
  return data as GetAuctionsResponse
}

interface GetAuctionResponse {
  data: IAuction
}

export const getAuction = async (
  id?: number,
  query: string = defaultAuctionPageQuery
) => {
  const response = await fetch(
    `${getApiBasepath()}/api/auctions/${id}?${query}`
  )
  const data = await response.json()
  return data as GetAuctionResponse
}

export function useAuction(
  id: number | undefined,
  config?: { initialData?: IAuction | null }
) {
  const { data: auction, ...rest } = useQuery({
    queryKey: ['auction', id],
    queryFn: async () => {
      if (!id) return null
      const { data } = await getAuction(id)
      if (!data) return null
      const flattened = flatCollectionType(data)
      return {
        ...flattened,
        status: determineAuctionStatus(flattened),
        url: getAuctionUrl(
          flattened.artistName || '',
          flattened.artworkName || '',
          flattened.id
        ),
      }
    },
    initialData: config?.initialData
      ? {
          ...flatCollectionType(config.initialData),
          status: determineAuctionStatus(config.initialData.attributes),
          url: getAuctionUrl(
            config.initialData.attributes.artistName || '',
            config.initialData.attributes.artworkName || '',
            config.initialData.id
          ),
        }
      : null,
  })

  return { ...rest, auction }
}

interface CreateInitialAuctionBidResponse {
  message: string
  success: boolean
  data: IAuctionBid['attributes'] & { id: IAuctionBid['id'] }
}

/**
 * Creates the initial bid (pending) for an auction.
 * @param auctionId
 * @param price CENTS
 * @returns
 */
export const createInitialAuctionBid = async (
  auctionId: number,
  price: number
) => {
  const response = await fetch(`${getApiBasepath()}/api/auction-bids`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    body: JSON.stringify({
      auctionId,
      price,
    }),
  })
  const data = await response.json()
  return data as CreateInitialAuctionBidResponse
}

interface PlaceAuctionBidResponse {
  message: string
  success: boolean
  data: IAuctionBid['attributes'] & { id: IAuctionBid['id'] }
  status: number
}

/**
 * Confirms the pending auction bid.
 * @param bidId The pending auction bid.
 * @returns
 */
export const placeAuctionBid = async (bidId: number) => {
  const response = await fetch(
    `${getApiBasepath()}/api/auction-bids/place/${bidId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  )
  const data = await response.json()
  return { ...data, status: response.status } as PlaceAuctionBidResponse
}

interface GetCurrentUsersAuctionBid {
  data?: FlatCollectionType<IAuctionBid>
  message: string
  success: boolean
}

export const getCurrentUsersAuctionBid = async () => {
  const response = await fetch(
    `${getApiBasepath()}/api/auction-bids/user/highest-bid`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  )
  const data = await response.json()
  return data as GetCurrentUsersAuctionBid
}

export function useCurrentUsersAuctionBid() {
  const { user } = useAuth()

  const { data: bid, ...rest } = useQuery({
    queryKey: ['auction-bid', user?.id],
    enabled: Boolean(user),
    queryFn: async () => {
      const { data } = await getCurrentUsersAuctionBid()
      return data
    },
  })

  return { bid, ...rest }
}

export const getUpcomingAuction = async () => {
  const currentDate = dayjs()
  const maxDate = currentDate.add(3, 'days')

  const query = qs.stringify(
    {
      filters: {
        startDate: {
          $gte: currentDate.toISOString(),
          $lte: maxDate.toISOString(),
        },
      },
      pagination: { pageSize: 1, page: 1 },
      populate: {
        artworkImages: {
          fields: ['name', 'formats', 'url'],
        },
      },
      sort: ['startDate:asc'],
    },
    { encodeValuesOnly: true }
  )
  const { data: auctions } = await getAuctions(query)
  if (!auctions?.length) return null

  const [auction] = auctions
  if (!auction) return null

  return auction
}

export const getOngoingAuction = async () => {
  const currentDate = dayjs()

  const query = qs.stringify(
    {
      filters: {
        startDate: {
          $lt: currentDate.toISOString(),
        },
        endDate: {
          $gt: currentDate.toISOString(),
        },
      },
      pagination: { pageSize: 1, page: 1 },
      populate: {
        artworkImages: {
          fields: ['name', 'formats', 'url'],
        },
      },
      sort: ['startDate:asc'],
    },
    { encodeValuesOnly: true }
  )
  const { data: auctions } = await getAuctions(query)
  if (!auctions?.length) return null

  const [auction] = auctions
  if (!auction) return null

  return auction
}

export function useUpcomingAuction() {
  const { featureFlags } = useFeatureFlags()

  const { data: upcomingAuction, ...rest } = useQuery({
    enabled: featureFlags?.['enable-auction'],
    queryKey: ['upcoming-auction'],
    queryFn: async () => {
      const auction = await getUpcomingAuction()
      if (!auction) return null

      const flatAuction = flatCollectionType(auction)
      const mappedAuction = {
        ...flatAuction,
        status: determineAuctionStatus(flatAuction),
      }

      if (!mappedAuction?.status?.pending) return null

      return mappedAuction
    },
  })

  return { upcomingAuction, ...rest }
}

export function useAvailableAuction() {
  const { featureFlags } = useFeatureFlags()

  const { data: availableAuction, ...rest } = useQuery({
    enabled: featureFlags?.['enable-auction'],
    queryKey: ['available-auction'],
    queryFn: async () => {
      // prioritize ongoing auction
      let auction: IAuction | null = await getOngoingAuction()

      // if no ongoing auction, try to find an upcoming auction
      if (!auction) {
        auction = await getUpcomingAuction()
      }

      // if neither found, return null
      if (!auction) return null

      const flatAuction = flatCollectionType(auction)
      const mappedAuction = {
        ...flatAuction,
        status: determineAuctionStatus(flatAuction),
      }

      if (mappedAuction?.status?.ended) return null

      return mappedAuction
    },
  })

  return { availableAuction, ...rest }
}

interface CreateAuctionAlertResponse {
  success: boolean
  message: string
}

export const createAuctionAlert = async (auctionId: number) => {
  const response = await fetch(`${getApiBasepath()}/api/auction-alerts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    },
    body: JSON.stringify({
      auctionId,
    }),
  })
  const data = await response.json()
  return data as CreateAuctionAlertResponse
}

export function useCreateAuctionAlert() {
  const qk = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (auctionId: number) => {
      const res = await createAuctionAlert(auctionId)
      if (!res.success) throw new Error(res.message)
      return res
    },
    onSuccess: async () => {
      await qk.invalidateQueries({ queryKey: ['auction-alert'] })
    },
  })

  return {
    ...mutation,
    createAuctionAlert: mutation.mutateAsync,
  }
}

interface GetExistingAuctionAlertResponse {
  success: boolean
  message: string
  data: unknown
}

export const getExistingAuctionAlert = async (auctionId: number) => {
  const response = await fetch(
    `${getApiBasepath()}/api/auction-alerts/is-alert-set/${auctionId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  )
  const data = await response.json()
  return data as GetExistingAuctionAlertResponse
}

export function useExistingAuctionAlert(auctionId: number | undefined) {
  const { user } = useAuth()

  const { data: existingAlert, ...rest } = useQuery({
    queryKey: ['auction-alert', auctionId, user?.id],
    enabled: Boolean(user && auctionId),
    queryFn: async () => {
      if (!auctionId) return null
      const { data } = await getExistingAuctionAlert(auctionId)
      return data
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: Infinity,
  })

  return { existingAlert, ...rest }
}

interface UploadAuctionBidIdentityDocumentsRequest {
  bidId: number
  files: File[] | FileList
}

interface UploadAuctionBidIdentityDocumentsResponse {
  success: boolean
  message: string
}

export const uploadAuctionBidIdentityDocuments = async ({
  bidId,
  files,
}: UploadAuctionBidIdentityDocumentsRequest) => {
  const formData = new FormData()
  const filesArray = [...files]
  filesArray.forEach(file => formData.append('files', file))

  const response = await fetch(
    `${getApiBasepath()}/api/auction-bids/${bidId}/documents`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: formData,
    }
  )
  const data = await response.json()
  return data as UploadAuctionBidIdentityDocumentsResponse
}

export function useUploadAuctionBidIdentityDocuments() {
  const mutation = useMutation({
    mutationFn: async (payload: UploadAuctionBidIdentityDocumentsRequest) => {
      const res = await uploadAuctionBidIdentityDocuments(payload)
      if (!res.success) throw new Error(res.message)
      return res
    },
  })

  return {
    ...mutation,
    uploadAuctionBidIdentityDocuments: mutation.mutateAsync,
  }
}
